<template>
  <div>
    <b-row class="pb-50">
      <!-- total count per subscription type  -->
      <b-col lg="7">
        <b-card
          no-body
          class="card-statistics"
          style="height: 255px;"
        >
          <b-card-header>
            <h2>{{ $t('Statistics') }}</h2>
          </b-card-header>
          <b-card-body class="statistics-body">
            <b-row>
              <b-col
                v-for="item in items.models"
                :key="item.category"
                xl="6"
                class="pb-1"
              >
                <b-media no-body>
                  <b-media-aside class="mr-2">
                    <b-avatar
                      v-if="item.category == 1"
                      size="48"
                      :variant="'light-primary'"
                    >
                      <feather-icon
                        size="24"
                        :icon="'TrendingUpIcon'"
                      />
                    </b-avatar>
                    <b-avatar
                      v-if="item.category == 2"
                      size="48"
                      :variant="'light-danger'"
                    >
                      <feather-icon
                        size="24"
                        :icon="'BoxIcon'"
                      />
                    </b-avatar>
                    <b-avatar
                      v-if="item.category == 3"
                      size="48"
                      :variant="'light-info'"
                    >
                      <feather-icon
                        size="24"
                        :icon="'UserIcon'"
                      />
                    </b-avatar>
                  </b-media-aside>
                  <b-media-body class="my-auto">
                    <h2 class="font-weight-bolder mb-0 text-primary">
                      {{ item.plan }}
                    </h2>
                    <b-card-text class="h3 mb-0">
                      {{ item.count }}
                    </b-card-text>
                  </b-media-body>
                </b-media>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- total count for all clients and per activity type  -->
      <b-col
        lg="5"
        class="d-flex justify-content-between flex-column"
      >
        <b-card class="card-congratulation-medal">
          <h2 class="pb-1">
            {{ $t('totalUsers') }}
          </h2>
          <h1 class="mb-75 mt-2 pt-50">
            <b-link style="font-size: 50px;">
              {{ items.total || 0 }}
            </b-link>
          </h1>
          <b-img
            :src="require('@/assets/images/illustration/badge.svg')"
            class="congratulation-medal"
            alt="Medal Pic"
          />
          <b-row class="bg-light p-1 round shadow-sm">
            <b-col md="6">
              <p class="card-text text-success mb-50 h4">
                {{ getActiveClientsStateLabel }}
              </p>
              <div
                class="progress bg-light-primary mt-25"
                style="height: 15px"
              >
                <div
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow="80"
                  class="progress-bar progress-bar-animated progress-bar-striped bg-success"
                  style="width: 80%"
                />
              </div>
            </b-col>
            <b-col md="6">
              <p class="card-text text-danger mb-50 h4">
                {{ getInactiveClientsStateLabel }}
              </p>
              <div
                class="progress bg-light-danger mt-25"
                style="height: 15px"
              >
                <div
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow="50"
                  class="progress-bar progress-bar-animated progress-bar-striped bg-danger"
                  style="width: 50%"
                />
              </div>
            </b-col>
          </b-row>
        </b-card>
      </b-col>

      <!-- top 10 clients have students -->
      <b-col
        lg="12"
        class="d-flex justify-content-between flex-column"
      >
        <b-card>
          <b-row>
            <b-card-header>
              <h2 class="mb-2">
                {{ $t("topTenClientsStudents") }}
              </h2>
            </b-card-header>
            <b-col md="12">
              <vue-apex-charts
                type="bar"
                height="450"
                :options="itemsBar.chartOptions"
                :series="itemsBar.series"
                :dir="isRight ? 'rtl' : 'ltr'"
              />
            </b-col>
          </b-row>
        </b-card>
      </b-col>

      <!-- number of users, students, invoices and collection vouchers  -->
      <b-col
        lg="12"
      >
        <b-card>
          <div class="row avg-sessions p-2">
            <div class="mb-2 col-6">
              <p class="card-text mb-50 h4">
                {{ $t("totalUsersForClients") }} : {{ totals.usersCount }}
              </p>
              <div
                class="progress bg-light-primary"
                style="height: 15px"
              >
                <div
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow="50"
                  class="progress-bar progress-bar-animated progress-bar-striped"
                  style="width: 50%"
                />
              </div>
            </div>
            <div class="mb-2 col-6">
              <p class="card-text mb-50 h4">
                {{ $t("totalStudentsForClients") }} : {{ totals.studentsCount }}
              </p>
              <div
                class="progress bg-light-warning"
                style="height: 15px"
              >
                <div
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow="60"
                  class="progress-bar progress-bar-animated progress-bar-striped bg-warning"
                  style="width: 60%"
                />
              </div>
            </div>
            <div class="mb-2 col-6">
              <p class="card-text mb-50 h4">
                {{ $t("totalInvoicesForClients") }} : {{ totals.invoicesCount }}
              </p>
              <div
                class="progress bg-light-secondary"
                style="height: 15px"
              >
                <div
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow="60"
                  class="progress-bar progress-bar-animated progress-bar-striped bg-instagram"
                  style="width: 60%"
                />
              </div>
            </div>
            <div class="col-6">
              <p class="card-text mb-50 h4">
                {{ $t("totalCollections") }} : {{ totals.collectionsCount }}
              </p>
              <div
                class="progress bg-light-success mt-25"
                style="height: 15px"
              >
                <div
                  role="progressbar"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow="90"
                  class="progress-bar progress-bar-animated progress-bar-striped bg-success"
                  style="width: 90%"
                />
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { plans } from '@/libs/acl/Lookups';
import VueApexCharts from 'vue-apexcharts';
import { $themeColors } from '@themeConfig';

export default {
  components: {
    VueApexCharts,
  },
  data() {
    return {
      items: {
        activeClientsCount: 0,
        activeClientsPercentage: 0,
        inactiveClientsCount: 0,
        inactiveClientsPercentage: 0
      },
      topClientsStudentsCounts: {},
      totals: {},
      plans: plans,
      itemsBar: {
        chartOptions: {
          chart: {
            type: 'bar',
            height: 450,
          },
          colors: [$themeColors.primary],
        },
      },
    }
  },
  computed: {
    getActiveClientsStateLabel() {
      return `${this.$t('activeClientsCount')} : ${this.items.activeClientsCount} - ( ${this.items.activeClientsPercentage}% )`
    },
    getInactiveClientsStateLabel() {
      return `${this.$t('inactiveClientsCount')} : ${this.items.inactiveClientsCount} - ( ${this.items.inactiveClientsPercentage}% )`
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.get({ url: 'Dashboard' }).then((data) => {
        this.items = data;
        this.topClientsStudentsCounts = data.clientsStudentsCounts;
        this.totals = data.statistics;

        const totalClients = data.activeClientsCount + data.inactiveClientsCount
        this.items.activeClientsPercentage = ((data.activeClientsCount / totalClients) * 100).toFixed(2)
        this.items.inactiveClientsPercentage = ((data.inactiveClientsCount / totalClients) * 100).toFixed(2)
      }).then(() => {
        this.updateChart();
      });
    },
    updateChart() {
      this.itemsBar = {
        series: [
          {
            name: this.$t('quantity'),
            data: this.topClientsStudentsCounts.map((item) => ({
              x: item.clientName,
              y: item.count,
            })),
          },
        ],
        chartOptions: {
          chart: {
            type: 'bar',
            height: 500
          },
          dataLabels: {
            enabled: true,
            style: {
                fontSize: '18px',
                fontFamily: 'Cairo',
              },
          },
          yaxis: {
            labels: {
              show: false
            },
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '9px',
                fontFamily: 'Cairo'
              },
            },
          },
          fill: {
            opacity: 1
          },
          colors: [$themeColors.primary, $themeColors.warning],
          tooltip: {
            y: {
              formatter: (val) => {
                return `${val}`;
              },
            },
            theme: 'dark',
            style: {
                fontSize: '14px',
                fontFamily: 'Cairo'
              },
          },
        },
      };
    }
  }
}
</script>
